import { useEffect, useState } from 'react'
import { Coins, Star, RefreshCw, ChevronDown } from 'lucide-react'
import { getAdminStarsBalance, getAdminStarsRate } from '../../services/api'
import {
  C, Card, Btn, Pill, Row, Loading, ErrorLine, inputStyle, labelStyle, fmtDateTime, fmtTimeMs,
} from './adminUI'

// Карточки Telegram Stars (task.txt п.8, 2026-09-17): «Баланс Stars бота» и
// «Цена Telegram Stars» перенесены из раздела «Система» в раздел «Финансы».
// Логика не менялась — только место жительства (и общий файл для двух секций).

// Дата операции истории (мс epoch от Telegram). Пояс — как во всей админке
// (UTC+3, см. ADMIN_TZ_OFFSET_MIN в adminUI.jsx): раньше здесь был чистый UTC
// через getUTC*, и время расходилось с остальными датами карточки.
const isoUTC = (ms) => fmtTimeMs(ms)

// Человеческие подписи типов операций журнала Telegram (source/receiver) —
// см. _opType в TG-BOT/src/starsBalance.js. RU: панель видит только админ.
const STAR_OP_LABEL = {
  sale: 'подписка', in: 'поступление', refund: 'возврат Fragment',
  refund_out: 'возврат покупателю', withdraw: 'вывод (Fragment)',
  affiliate: 'аффилейт', affiliate_out: 'вывод аффилейту',
  ads: 'Telegram Ads', tg_fee: 'оплата Telegram', chat_in: 'перевод из чата',
  out: 'списание',
}
const STAR_OP_STATE = {
  mature: { t: 'созрела', c: C.green },
  frozen: { t: 'заморожена', c: C.warn },
  consumed: { t: 'списана', c: C.dim },
  out: { t: 'списание', c: C.dim },
}

// Звёзды с разделителями разрядов: 5 → «5», 769 → «769», 3461 → «3,461»,
// дробные (nanostar) — до 2 знаков: «1,234.56».
const fmtStars = (x) => {
  const n = Number(x) || 0
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

// Баланс Stars бота (задача №1 task.txt): getMyStarBalance + журнал Telegram
// → сколько ⭐ на аккаунте, сколько уже «созрело» (правило 21 дня) и когда
// разморозятся остальные. Карточка сама ходит в API (плюс к конфигу) и
// обновляется раз в минуту — ровно период кэша бэкенда.
export function StarsBalanceCard() {
  const [s, setS] = useState(null)
  const [err, setErr] = useState('')
  const [showHist, setShowHist] = useState(false)

  const load = async () => {
    try {
      const d = await getAdminStarsBalance()
      setS(d); setErr('')
    } catch (e) { setErr(`Не удалось загрузить баланс: ${e?.message || e}`) }
  }

  useEffect(() => { load() }, [])
  useEffect(() => {
    const t = setInterval(load, 60_000)
    return () => clearInterval(t)
  }, [])

  const telegram = s?.source === 'telegram'

  // Подпись к строке «Ещё заморожено»: заморозка бывает двух родов — по дате
  // (получены < 21 дн. назад) и «без даты в журнале» (возраст неизвестен).
  const heldSub = !s ? '' : (s.heldDated > 0
    ? `получены < ${s.holdDays ?? 21} дн. назад${s.allMatureAtHuman ? ` · до ${fmtDateTime(s.allMatureAtHuman)}` : ''}`
    : (s.unexplained > 0 ? 'возраст неизвестен — см. примечание' : 'нет'))

  return (
    <Card
      title="Баланс Stars бота"
      icon={Star}
      accent={C.warn}
      right={<Btn small ghost icon={RefreshCw} color={C.muted} onClick={load}>Обновить</Btn>}
    >
      {!s ? (
        err ? <ErrorLine onRetry={load}>{err}</ErrorLine> : <Loading />
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
            <Pill color={telegram ? C.green : C.warn}>
              {telegram ? 'данные Telegram' : 'оценка по нашему журналу'}
            </Pill>
            {!telegram && (
              <span style={{ fontSize: 10.5, color: C.dim }}>
                Telegram недоступен{s?.error ? ` (${s.error})` : ''} — не баланс аккаунта, а «продано по нашей базе»
              </span>
            )}
            {telegram && s?.journal?.sales > 0 && (
              <span style={{ fontSize: 10.5, color: C.dim }}>
                продажи по базе: {s.journal.sales} шт · {fmtStars(s.journal.recordedStars)} ⭐
              </span>
            )}
          </div>

          <Row
            label="На аккаунте сейчас"
            value={`${fmtStars(s.balance)} ⭐`}
            valueColor="#fff"
            valueSub={`≈ $${(Number(s.balance) * Number(s.usdPerStar || 0)).toFixed(2)} чистыми по курсу вывода`}
            mono
          />
          <Row
            label="Созрело — можно выводить"
            value={`${fmtStars(s.eligible)} ⭐`}
            valueColor={C.accent}
            valueSub={`только подтверждённые журналом · получены ≥ ${s.holdDays ?? 21} дн. назад`}
            mono
          />
          <Row
            label="Ещё заморожено"
            value={`${fmtStars(s.held)} ⭐`}
            valueColor={C.warn}
            valueSub={heldSub}
            mono
          />

          {s.unexplained > 0 && (
            <div style={{ fontSize: 11, color: C.warn, marginTop: 6, lineHeight: 1.45 }}>
              {fmtStars(s.unexplained)} ⭐ в балансе нет в журнале операций Telegram
              (только что поступили / лента ещё не подтянулась / поступление без записи) — возраст
              не подтверждён, поэтому они показаны <b style={{ color: '#fff' }}>замороженными</b>,
              а не доступными к выводу.
            </div>
          )}

          <div style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 10, color: C.muted }}>
            {s.canWithdraw ? (
              <span style={{ color: C.accent }}>
                Вывод доступен: созрело {fmtStars(s.eligible)} ⭐ (минимум 1 000 соблюдён).{' '}
                Вывод звёзд — через Fragment → TON (админ делает вручную, приложение звёзды не выводит).
              </span>
            ) : (
              <>
                Вывод начнётся только когда «созреет» 1 000 ⭐ (каждая звезда держится{' '}
                {s.holdDays ?? 21} день с момента получения). Сейчас созрело {fmtStars(s.eligible)}.
                {s.heldDated > 0 && s.firstThousandAtHuman ? (
                  <span> Первые 1 000 разморозятся к <b style={{ color: '#fff' }}>{fmtDateTime(s.firstThousandAtHuman)}</b>.</span>
                ) : s.held > 0 ? (
                  <span> Текущего остатка на 1 000 не хватит — добирайте продажами.</span>
                ) : null}
              </>
            )}
          </div>

          {s.ledgerEmpty && (
            <div style={{ fontSize: 11, color: C.warn, marginTop: 8, lineHeight: 1.45 }}>
              В журнале Telegram нет поступлений — возраст остатка не подтверждён, поэтому весь баланс
              показан <b style={{ color: '#fff' }}>замороженным</b> (а не «можно выводить»). Появятся
              операции — «Созрело» начнётся считаться по их датам.
            </div>
          )}
          {s.merchantNote && (
            <div style={{ fontSize: 11, color: C.warn, marginTop: 8, lineHeight: 1.45 }}>
              Баланс из Telegram = 0, но продажи по нашей базе были. Похоже, бот не активирован
              как мерчант Stars: BotFather → /mybots → Bot Settings → Payments → «Activate Stars».
            </div>
          )}

          {/* ── История операций (замечание владельца 2026-09-07) ─────────────── */}
          {Array.isArray(s.history) && s.history.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <button
                onClick={() => setShowHist(v => !v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '9px 10px', boxSizing: 'border-box',
                  background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.line}`,
                  borderRadius: 10, cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', flex: 1, textAlign: 'left' }}>
                  История операций
                </div>
                <div style={{ fontSize: 10.5, color: C.dim }}>
                  {telegram ? 'журнал Telegram' : 'наша база (продажи)'} · {s.history.length}
                </div>
                <ChevronDown
                  size={14}
                  color={C.dim}
                  style={{ flexShrink: 0, transform: showHist ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                />
              </button>

              {showHist && (
                <div style={{
                  maxHeight: 260, overflowY: 'auto', marginTop: 6,
                  background: 'rgba(0,0,0,0.25)', border: `1px solid ${C.line}`,
                  borderRadius: 10,
                }}>
                  {s.history.map((h, i) => {
                    const label = STAR_OP_LABEL[h.type] || 'операция'
                    const st = STAR_OP_STATE[h.state]
                    const plus = h.stars > 0
                    return (
                      <div
                        key={`${h.at}-${i}`}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '7px 10px', boxSizing: 'border-box',
                          borderBottom: i < s.history.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                        }}
                      >
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>{label}</div>
                          <div style={{ fontSize: 10, color: C.dim, marginTop: 1 }}>{isoUTC(h.at)}</div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{
                            fontSize: 12.5, fontWeight: 700, fontFamily: 'monospace',
                            color: plus ? C.green : C.danger,
                          }}>
                            {plus ? '+' : ''}{fmtStars(h.stars)} ⭐
                          </div>
                          {st ? (
                            <div style={{ fontSize: 9.5, color: st.c, marginTop: 1 }}>{st.t}</div>
                          ) : null}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {s.fetchedAt && (
            <div style={{ fontSize: 9.5, color: C.dim, marginTop: 8 }}>
              обновлено {fmtDateTime(s.fetchedAt)} · кэш 60 с · источник —{' '}
              {telegram ? 'аккаунт бота (getMyStarBalance)' : 'наша база (payment_requests)'}
            </div>
          )}
        </>
      )}
    </Card>
  )
}

// Стиль сегментной кнопки «Авто / Вручную» (был локальным в SystemSection).
const segBtn = (active) => ({
  flex: 1,
  padding: '9px 0',
  borderRadius: 10,
  border: active ? '1px solid rgba(16,185,129,0.55)' : '1px solid rgba(255,255,255,0.12)',
  fontSize: 12.5,
  fontWeight: 600,
  cursor: 'pointer',
  background: active ? 'rgba(16,185,129,0.14)' : 'transparent',
  color: active ? '#fff' : C.dim,
  WebkitTapHighlightColor: 'transparent',
})

// «Цена Telegram Stars» (task.txt, 2026-09-06): mode 'auto' — цена считается от
// КУРСА ПОКУПКИ звезды и цены тарифа (задача владельца 2026-10-03: юзер платит
// за подписку примерно её цену); mode 'manual' — админ задаёт фиксированную цену
// в ⭐ ДЛЯ ВСЕХ тарифов. Правки живут в cfg.starsPricing и сохраняются кнопкой
// «Сохранить настройки» внизу раздела (как в «Системе»).
//
// Курс на экране — наш, константа из TG-BOT/src/starsRate.js (Bot API курса не
// отдаёт), поэтому он подписан явно и здесь не редактируется. Цифры тарифов
// приходят с бэкенда (GET /api/admin/stars/rate) — второй копии формулы во
// фронте нет: что на кнопке у юзера, то и в таблице.

// Курс в виде «0.0199»: до 5 знаков, хвостовые нули убраны.
const fmtRate = (x) => Number(Number(x) || 0).toFixed(5).replace(/0+$/, '').replace(/\.$/, '')

export function StarsPricingCard({ cfg, setGroup, plans }) {
  const sp = cfg.starsPricing || {}
  const spMode = sp.mode === 'manual' ? 'manual' : 'auto'

  const [rate, setRate] = useState(null)
  const [err, setErr] = useState('')

  const load = async () => {
    try {
      const d = await getAdminStarsRate()
      setRate(d); setErr('')
    } catch (e) { setErr(`Не удалось загрузить курс: ${e?.message || e}`) }
  }

  useEffect(() => { load() }, [])
  useEffect(() => {
    const t = setInterval(load, 60_000)
    return () => clearInterval(t)
  }, [])

  // Лимит одной звёздной операции у Telegram — с бэкенда (10 000 ⭐), здесь
  // только страховка для поля ввода, пока ответ не пришёл.
  const limit = Number(rate?.invoiceLimit) || 10000
  // Запасной источник строк таблицы — тарифы, уже загруженные разделом
  // «Финансы» (getAdminPlans): колонка «придёт нам» останется пустой, но список
  // не пропадёт, пока не ответил эндпоинт курса.
  const planRows = Array.isArray(rate?.plans)
    ? rate.plans
    : (((plans || {}).data || {}).plans || []).map(p => ({
      code: p.code, title: p.title_ru, priceUsdt: p.price_usdt,
      priceStars: p.priceStars, buyerPaysUsd: p.priceStarsUsd, weGetUsd: null,
    }))

  return (
    <Card
      title="Цена Telegram Stars"
      icon={Coins}
      accent={C.warn}
      right={<Btn small ghost icon={RefreshCw} color={C.muted} onClick={load}>Обновить</Btn>}
    >
      {/* ── Курс ─────────────────────────────────────────────────────────── */}
      {!rate ? (
        err ? <ErrorLine onRetry={load}>{err}</ErrorLine> : <Loading />
      ) : (
        <>
          <Row
            label="Курс покупки — по нему цена тарифа"
            value={`1 ⭐ = $${fmtRate(rate.buyerUsdPerStar)} · $1 = ${Math.round(Number(rate.starsPerUsd) || 0)} ⭐`}
            valueColor="#fff"
            valueSub="столько звезда стоит юзеру в приложении (100 ⭐ = $1.99, 500 ⭐ = $9.99)"
            mono
          />
          <Row
            label="Наш курс — сколько приходит нам"
            value={`1 ⭐ = $${fmtRate(rate.netUsdPerStar)} · $1 = ${Math.round(Number(rate.starsPerUsdNet) || 0)} ⭐`}
            valueColor={C.accent}
            valueSub={`рыночный $${fmtRate(rate.marketUsdPerStar)} минус ${rate.withdrawFeePct}% на выводе (Fragment → TON)`}
            mono
          />
          {err ? <ErrorLine onRetry={load}>{err}</ErrorLine> : null}

          {/* ── Тарифы: что увидит юзер и что останется нам ─────────────────── */}
          {rate.mode === 'manual' ? (
            <div style={{ fontSize: 11.5, color: C.warn, lineHeight: 1.5, margin: '10px 0 4px' }}>
              Ручной режим: все тарифы стоят{' '}
              <b style={{ color: '#fff' }}>{fmtStars(rate.manualStars)} ⭐</b> — цена не связана с
              долларовой ценой тарифа. Юзер заплатит за любую подписку одну и ту же сумму.
            </div>
          ) : (
            <div style={{ marginTop: 10 }}>
              <div style={labelStyle}>Тарифы: цена в ⭐ считается как цена ÷ курс покупки</div>
              <div style={{ overflowX: 'auto', border: `1px solid ${C.line}`, borderRadius: 10 }}>
                <table style={{
                  width: '100%', borderCollapse: 'collapse', fontSize: 11,
                  fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                }}>
                  <thead>
                    <tr style={{ color: C.dim, background: 'rgba(255,255,255,0.04)' }}>
                      <th style={{ textAlign: 'left',  padding: '6px 8px', fontWeight: 600 }}>Тариф</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Цена</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Звёзд</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Заплатит юзер</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>Придёт нам</th>
                    </tr>
                  </thead>
                  <tbody>
                    {planRows.map((p, i) => (
                      <tr key={p.code || i}>
                        <td style={{ padding: '6px 8px', borderTop: `1px solid ${C.line}`, color: '#fff' }}>
                          {p.title || p.code}
                        </td>
                        <td style={{ padding: '6px 8px', borderTop: `1px solid ${C.line}`, color: C.muted }}>
                          ${Number(p.priceUsdt || 0).toFixed(2)}
                        </td>
                        <td style={{ padding: '6px 8px', borderTop: `1px solid ${C.line}`, color: '#fff', fontWeight: 700 }}>
                          {fmtStars(p.priceStars)} ⭐
                          {p.overLimit ? <span style={{ color: C.danger }}> · сверх лимита</span> : null}
                        </td>
                        <td style={{ padding: '6px 8px', borderTop: `1px solid ${C.line}`, color: C.muted }}>
                          ${Number(p.buyerPaysUsd || 0).toFixed(2)}
                        </td>
                        <td style={{
                          padding: '6px 8px', borderTop: `1px solid ${C.line}`,
                          color: p.weGetUsd === null ? C.dim : C.accent,
                        }}>
                          {p.weGetUsd === null || p.weGetUsd === undefined
                            ? '—'
                            : `$${Number(p.weGetUsd).toFixed(2)}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 10, color: C.dim, marginTop: 5, lineHeight: 1.45 }}>
                «Заплатит юзер» — по курсу покупки, «придёт нам» — по нашему курсу. Разницу съедает
                не Telegram (за приём звёзд он не берёт ничего), а спред между покупкой и выводом.
                Счета, выставленные до правки, не меняются.
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Режим цены ───────────────────────────────────────────────────── */}
      <div style={{ ...labelStyle, marginTop: 14 }}>Режим цены</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button onClick={() => setGroup('starsPricing', 'mode', 'auto')} style={segBtn(spMode === 'auto')}>
          Авто · по курсу
        </button>
        <button onClick={() => setGroup('starsPricing', 'mode', 'manual')} style={segBtn(spMode === 'manual')}>
          Вручную
        </button>
      </div>
      {spMode === 'manual' && (
        <div style={{ paddingBottom: 8 }}>
          <div style={labelStyle}>Цена в ⭐ (для всех тарифов)</div>
          <input
            type="number"
            min={1}
            max={limit}
            value={cfg.starsPricing?.manualStars ?? 5}
            onChange={e => {
              const v = e.target.value
              setGroup(
                'starsPricing',
                'manualStars',
                v === '' ? '' : Math.min(limit, Math.max(1, Math.round(Number(v) || 0))),
              )
            }}
            style={inputStyle}
          />
          <div style={{ fontSize: 10, color: C.dim, marginTop: 4 }}>
            Максимум {fmtStars(limit)} ⭐ — лимит одной операции Telegram.
          </div>
        </div>
      )}
      <div style={{ fontSize: 10.5, color: C.dim, lineHeight: 1.45 }}>
        <b style={{ color: C.muted }}>Авто</b> — цена тарифа в ⭐ = цена тарифа ÷ курс покупки
        (округление до целой звезды), у разных тарифов разная. <b style={{ color: C.muted }}>Вручную</b> —
        все тарифы стоят заданное число ⭐ (например <b style={{ color: C.muted }}>5</b> для дешёвого
        теста). Ручной режим временный: оплата идёт по фактической стоимости звёзд, без связи
        с долларовой ценой тарифа. Сохраните кнопкой внизу.
      </div>
      {rate && (
        <div style={{ fontSize: 9.5, color: C.dim, marginTop: 8 }}>
          действующий режим цены: {rate.mode === 'manual'
            ? `вручную, ${fmtStars(rate.manualStars)} ⭐`
            : 'авто, по курсу покупки'} · курс — константа в TG-BOT/src/starsRate.js
        </div>
      )}
    </Card>
  )
}
